import {
    Grid2 as Grid,
    Stack,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { I18nStore } from '@tools/i18n/store';
import { injectTSDI } from '@tools/tsdi';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { CountryPicker } from '../components/country-picker';
import { ProcessingOptIn } from '../components/opt-ins';
import DefaultPasswordField from '../components/password-field';
import DefaultPhoneField from '../components/phone-field';
import { StepButtons } from '../components/step-buttons';
import { StudioNameField } from '../components/studio-name-field';
import DefaultTextField from '../components/text-field';
import { SignupStore } from '../signup-store';
import { studioLocations } from '../steps-data';
import { Step2Store } from './store';
import { useRecaptcha } from './useRecaptcha';

const isLocal = () => window.location.host.includes('local');
const isDev = () => window.location.host.includes('dev');

const SectionTitle = styled(Typography)({
    paddingBottom: 16,
    paddingTop: 40
});

const CaptchaContainer = styled('div')({
    margin: '0 auto'
});

export const SignupStep2 = observer(() => {
    const { translate, hasTranslation, language } = injectTSDI(I18nStore);
    const stepStore = injectTSDI(Step2Store);
    const { addtrackingProps } = injectTSDI(SignupStore);
    const { isDomainSuggestionLoading, onNextStep, form } = stepStore;
    const { phone, email, password, country } = form.$;

    const theme = useTheme();
    const biggerScreens = useMediaQuery(theme.breakpoints.up('sm'));

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const {
        shouldShowCaptchaV2,
        captchaRef,
        isCaptchaChecked,
        setIsCaptchaChecked
    } = useRecaptcha();

    const handleCaptchaChange = (token: string | null) => {
        if (token) {
            stepStore.widgetToken = token;
            setIsCaptchaChecked(true);
        }
    };

    React.useEffect(() => {
        addtrackingProps({
            pageCategory: 'onboarding',
            pageName: 'Registration data',
            lang: language
        });
    }, [addtrackingProps, language]);

    React.useEffect(() => {
        if (isSubmitting && !isDomainSuggestionLoading) {
            void (async () => {
                await onNextStep();
                setIsSubmitting(false);
            })();
        }
    }, [isSubmitting, onNextStep, isDomainSuggestionLoading]);

    const formSubmitHandler = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
    };

    return (
        <form onSubmit={formSubmitHandler}>
            <Typography variant="h4">
                {translate('signup.data.for.free.demo')}
            </Typography>

            <SectionTitle variant="subtitle1">
                {translate('signup.personal.data')}:
            </SectionTitle>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DefaultTextField
                        autoComplete="email"
                        label={translate('signup.email')}
                        type="email"
                        field={email}
                        dataRole="email"
                        required
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DefaultPasswordField
                        required
                        field={password}
                        helperText={translate(
                            'signup.password.criteria.helper.text'
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DefaultPhoneField
                        label={translate('commons.phone')}
                        field={phone}
                        autoComplete="tel"
                        helperText={
                            hasTranslation('signup.form.phone.helper.text')
                                ? translate('signup.form.phone.helper.text')
                                : undefined
                        }
                    />
                </Grid>
                {biggerScreens && <Grid size={6}></Grid>}
            </Grid>
            <SectionTitle variant="subtitle1">
                {translate('signup.studio.data')}:
            </SectionTitle>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <StudioNameField />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <CountryPicker
                        field={country}
                        countries={studioLocations}
                        label={translate('signup.country')}
                    />
                </Grid>
            </Grid>

            <Stack>
                <ProcessingOptIn />
                <Typography
                    variant="caption"
                    color={form.hasError ? 'error' : 'textSecondary'}
                >
                    *{translate('commons.required')}
                </Typography>
                {shouldShowCaptchaV2 && (
                    <CaptchaContainer>
                        <ReCAPTCHA
                            onChange={handleCaptchaChange}
                            sitekey={
                                isLocal() || isDev()
                                    ? process.env.RECAPTCHAV2_TESTSITEKEY
                                    : process.env.RECAPTCHAV2_SITEKEY
                            }
                        />
                    </CaptchaContainer>
                )}
                <input
                    ref={captchaRef}
                    type="hidden"
                    id="captchatoken"
                    name="captchatoken"
                    value=""
                />
            </Stack>
            <StepButtons
                nextBtnLabel="signup.step2.next.button.label"
                hasBackBtn
                backBtnEventName="back_to_welcome"
                data-role="submit-button"
                loading={isSubmitting}
                disabled={shouldShowCaptchaV2 && !isCaptchaChecked}
                type="submit"
            />
        </form>
    );
});
